import { useCallback, useMemo, useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { navStore, useStore } from '@mcn-platform/models';
import { DELETE_FOREVER_PHRASE } from 'components/deletion/DeleteForeverDialog';
import type { PortalDeletionPreviewData } from 'components/deletion/PortalDeleteDialog';
import { formatGraphqlError } from 'utils/graphqlErrors';
import {
  deletePortalPermanently,
  previewPortalDeletion,
} from 'components/deletion/tenantDeletionGraphql';

export type PortalDeleteFlowState = {
  portal: { id: string; name?: string };
  loading: boolean;
  loadError?: string;
  previewData: PortalDeletionPreviewData | null;
} | null;

const refreshPortalList = (store: ReturnType<typeof useStore>) => {
  // Reset AND refetch all cached `portal` lists so the visible list (held in
  // ItemNavigator state) reloads instead of getting stuck at `stubbed`.
  store.reloadCachedListsForModel('portal');
};

export const usePortalDeletion = () => {
  const store = useStore();
  const organizationID = store.currentOrganization?.id;
  const isSuperUser = store.currentUserRoles?.includes('SUPERUSER') ?? false;
  const [deleteFlow, setDeleteFlow] = useState<PortalDeleteFlowState>(null);

  const openDeleteFlow = useCallback(async (portal: { id: string; name?: string }) => {
    if (!organizationID) return;
    setDeleteFlow({ portal, loading: true, previewData: null });
    try {
      const result: any = await API.graphql(
        graphqlOperation(previewPortalDeletion, {
          organizationID,
          portalID: portal.id,
        }),
      );
      setDeleteFlow({
        portal,
        loading: false,
        previewData: result?.data?.previewPortalDeletion ?? null,
      });
    } catch (err: unknown) {
      setDeleteFlow({
        portal,
        loading: false,
        loadError: formatGraphqlError(err),
        previewData: null,
      });
    }
  }, [organizationID]);

  const handleDeletePortal = async () => {
    if (!deleteFlow?.portal || !organizationID) return;
    const { portal } = deleteFlow;
    try {
      await API.graphql(graphqlOperation(deletePortalPermanently, {
        organizationID,
        portalID: portal.id,
        confirmationPhrase: DELETE_FOREVER_PHRASE,
      }));
      navStore.showSnackBar('Portal deleted.', 'success');
      if (store.currentPortal?.id === portal.id) {
        const list = store.getCachedList(
          'portal',
          { organizationID },
          { field: 'name', direction: 'asc' },
        );
        const other = list.items.find(
          (p: { id: string; status?: string }) => p.id !== portal.id
            && (p.status || 'ACTIVE') === 'ACTIVE',
        );
        if (other) store.setCurrentPortal(other);
      }
      refreshPortalList(store);
    } catch (err: unknown) {
      navStore.showErrorDialog('Delete failed', formatGraphqlError(err));
    }
  };

  const deletionActions = useMemo(() => {
    if (!isSuperUser) return [];
    return [
      {
        name: 'deletePortal',
        label: 'Delete',
        action: (portal: { id: string; name?: string }) => { openDeleteFlow(portal); },
        hidden: (portal: { status?: string }) => (portal.status || 'ACTIVE') !== 'ACTIVE',
        disabled: () => !organizationID,
      },
    ];
  }, [isSuperUser, organizationID, openDeleteFlow]);

  return {
    deletionActions,
    deleteFlow,
    setDeleteFlow,
    handleDeletePortal,
  };
};
